import { formatCurrency, membershipHelper, ordinal } from "@gymflow/helpers";
import { MembershipBean } from "@gymflow/types";
import { XIcon } from "apps/portal/src/components/atoms/icons/XIcon";
import { capitalize } from "lodash";
import { DateTime, Duration } from "luxon";
import { useContext } from "react";
import { useTranslation } from "react-i18next";

import {
  ModalContext,
  ModalWrapper,
  useClubSettings,
} from "../../../providers";
import { AlertCircleIcon, Button, HelpCircleIcon } from "../../atoms";

const ProratedMembershipModal = ({
  membership,
  startDate,
  billingDay,
}: {
  membership: MembershipBean;
  startDate: string;
  billingDay: string;
}) => {
  const { t } = useTranslation();
  const {
    propertiesSettings: { club },
  } = useClubSettings();
  const daysUntilBilling = membershipHelper.daysFromStartDateToNextBillingDate(
    membership,
    startDate,
  );
  const { popModal } = useContext(ModalContext);
  const window = Duration.fromObject({
    [membershipHelper.billingTypeToTimeUnit[membership.billingType!]]:
      membership.billingPeriod,
  }).toHuman();
  return (
    <ModalWrapper
      onCancel={() => {
        popModal();
      }}
      className="flex flex-col gap-4 px-4 pt-6"
    >
      <div className="flex flex-col">
        <div className="flex flex-row items-center justify-between">
          <div className="text-lg font-semibold text-gray-950">
            {t("pages.hostedPagesCheckout.prorationModal.title")}
          </div>
          <Button
            onClick={() => {
              popModal();
            }}
            className="cursor-pointer border-0 bg-transparent p-0"
            size="icon"
          >
            <XIcon className="size-6" pathClassName="stroke-gray-500" />
          </Button>
        </div>
        <div className="text-base font-medium text-gray-500">
          {t("pages.hostedPagesCheckout.prorationModal.description")}
        </div>
      </div>
      <div className="flex flex-col gap-2 rounded-[10px] border border-gray-200 px-3 py-2">
        <div className="flex flex-row justify-between">
          <div className="text-sm font-medium text-gray-500">
            {t("pages.hostedPagesCheckout.prorationModal.numberOfDaysProrated")}
          </div>
          <div className="text-sm font-medium text-gray-950">
            {t("portal.common.days", {
              daysUntilBilling,
            })}
          </div>
        </div>
        <div className="w-full border-b border-gray-200" />
        <div className="flex flex-row justify-between">
          <div className="text-sm font-medium text-gray-500">
            {t(
              "pages.hostedPagesCheckout.prorationModal.membershipBillingDate",
            )}
          </div>
          <div className="text-sm font-medium text-gray-950">
            {membership.billingType === "WEEKLY"
              ? billingDay
              : t(
                  "pages.hostedPagesCheckout.prorationModal.monthlyBillingDay",
                  {
                    day: billingDay,
                  },
                )}
          </div>
        </div>
        <div className="flex flex-row justify-between">
          <div className="text-sm font-medium text-gray-500">
            {t("pages.hostedPagesCheckout.prorationModal.membershipAmount")}
          </div>
          <div className="text-sm font-medium text-gray-950">
            {formatCurrency(membership.defaultPrice, club.defaultCurrency)}
          </div>
        </div>
      </div>
      <div className="text-sm font-medium">
        {t("pages.hostedPagesCheckout.prorationModal.youWillBeBilled", {
          day:
            membership.billingType === "WEEKLY"
              ? billingDay
              : t("portal.common.the", {
                  day: billingDay,
                }),
          window,
        })}
      </div>
    </ModalWrapper>
  );
};

export const HostedCheckoutProratedMembershipBanner = ({
  membership,
  startDate,
}: {
  membership: MembershipBean;
  startDate: string;
}) => {
  const {
    propertiesSettings: { club },
  } = useClubSettings();
  const { t } = useTranslation();
  const { stackModal } = useContext(ModalContext);
  const daysUntilBilling = membershipHelper.daysFromStartDateToNextBillingDate(
    membership,
    startDate,
  );

  if (membership.type === "PREPAID" || membership.calculateProrata === "NO") {
    return null;
  }
  const price = formatCurrency(membership.defaultPrice, club.defaultCurrency);
  const window = Duration.fromObject({
    [membershipHelper.billingTypeToTimeUnit[membership.billingType!]]:
      membership.billingPeriod,
  }).toHuman();
  const billingDay = membership.weeklyBillingDay
    ? DateTime.fromFormat(
        capitalize(membership.weeklyBillingDay),
        "cccc",
      ).toFormat("cccc")
    : membership.monthlyBillingDay
      ? ordinal(
          +DateTime.fromFormat(
            membership.monthlyBillingDay?.toString() ?? "",
            "d",
          ).toFormat("d"),
        )
      : "";
  return (
    <div className="flex w-full flex-row items-center gap-2">
      <div className="flex flex-1 flex-row gap-2 rounded-xl bg-gray-100 px-3 py-2">
        <AlertCircleIcon className="size-5" pathClassName="stroke-gray-500" />
        <div className="flex-1 text-sm font-medium text-gray-950">
          {membership.calculateProrata === "YES"
            ? t("pages.hostedPagesCheckout.proratedMembership", {
                price,
                window,
                billingDay:
                  membership.billingType === "WEEKLY"
                    ? billingDay
                    : t("portal.common.the", {
                        day: billingDay,
                      }),
              })
            : t("pages.hostedPagesCheckout.proratedMembershipWaived", {
                price,
                window,
                billingDay:
                  membership.billingType === "WEEKLY"
                    ? billingDay
                    : t("portal.common.the", {
                        day: billingDay,
                      }),
              })}
        </div>
      </div>
      {membership.calculateProrata === "YES" && daysUntilBilling > 0 && (
        <Button
          size="icon"
          className="cursor-pointer border-0 bg-transparent p-1"
          onClick={() => {
            stackModal(
              <ProratedMembershipModal
                membership={membership}
                startDate={startDate}
                billingDay={billingDay}
              />,
            );
          }}
        >
          <HelpCircleIcon className="size-5" pathClassName="stroke-gray-500" />
        </Button>
      )}
    </div>
  );
};
